import React, { FC, ReactNode, Suspense, useEffect, useMemo } from 'react';

import { importRemote } from '@module-federation/utilities';
import PropTypes from 'prop-types';

import { TOTAL_REMOTE_LOADING_TIME } from '@adsk/exo-skeleton-api';
import { useRemote } from '@adsk/exo-skeleton-react';

import {
  abortTracking,
  REMOTE_TRACKING_TIMEOUT,
  type PerformanceTrackingAbortReasons,
} from '../../utils/performance';
import { removeFileNameFromURL } from '../../utils/removeFileNameFromURL';
import AppContentLoader from '../AppContentLoader';

const REMOTE_ENTRY_FILENAME = 'remoteEntry.js';

export type LazyComponentProps = {
  url: string;
  module: string;
  scope: string;
  renderLoader?: () => NonNullable<ReactNode> | null;
  children?: ReactNode;
};

const LazyComponent: FC<LazyComponentProps> = React.memo(
  ({
    renderLoader = ({
      scope,
      moduleName,
    }: {
      scope: string;
      moduleName: string;
    }) => <AppContentLoader scope={scope} moduleName={moduleName} />,
    children,
    ...props
  }) => {
    const { url, module, scope } = props;
    const { trackLoading } = useRemote();

    const abortTrackingHandler = ({
      reason,
    }: {
      reason: PerformanceTrackingAbortReasons;
    }) =>
      abortTracking({
        scope,
        moduleName: module,
        trackingId: TOTAL_REMOTE_LOADING_TIME,
        reason,
      });

    useEffect(() => {
      trackLoading();

      const timeout = setTimeout(() => {
        void abortTrackingHandler({ reason: 'timeout' });
      }, REMOTE_TRACKING_TIMEOUT);

      return () => {
        void abortTrackingHandler({ reason: 'unmount' });
        clearTimeout(timeout);
      };

      // This use effect should behave as unmount
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const Component = useMemo(
      () =>
        React.lazy(() =>
          importRemote({
            url: removeFileNameFromURL(url, REMOTE_ENTRY_FILENAME),
            module,
            scope,
            bustRemoteEntryCache: false,
            remoteEntryFileName: REMOTE_ENTRY_FILENAME,
          }),
        ),
      [url, module, scope],
    );

    return (
      <Suspense fallback={renderLoader({ scope, moduleName: module })}>
        <Component {...props}>{children}</Component>
      </Suspense>
    );
  },
);

LazyComponent.displayName = 'LazyComponent';
LazyComponent.propTypes = {
  url: PropTypes.string.isRequired,
  module: PropTypes.string.isRequired,
  scope: PropTypes.string.isRequired,
  renderLoader: PropTypes.func,
  children: PropTypes.node,
};

export default LazyComponent;
